"use client";

/**
 * Info ⓘ icon with a viewport-fixed popover tooltip.
 *
 * Extracted from the prototype `compass-intake.jsx:803-812`.
 * Uses `createPortal` for the tooltip so it escapes overflow clipping
 * (e.g. inside the rail). Replaces inline CSS with Tailwind utilities.
 */

import { type CSSProperties, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "../lib/icons";

export interface InfoDotProps {
  /** Tooltip text shown on hover / focus. */
  text: string;
  /**
   * Set to `true` when the trigger sits on a dark/coloured background
   * (e.g. the provenance pill in CiteDetail) so the icon is white.
   * Defaults to `false` (icon uses `--fg-muted`, visible on white/canvas).
   */
  onDark?: boolean;
}

const TIP_WIDTH = 262;
const MARGIN = 10;

/**
 * Compact info icon that reveals a portal-rendered tooltip on hover / focus.
 * The tooltip is horizontally clamped to the viewport so it never overflows.
 */
export function InfoDot({ text, onDark = false }: InfoDotProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [tipStyle, setTipStyle] = useState<CSSProperties | null>(null);

  function show() {
    const el = ref.current;
    if (!el) return;
    const b = el.getBoundingClientRect();
    let left = b.left + b.width / 2;
    left = Math.max(
      TIP_WIDTH / 2 + MARGIN,
      Math.min(left, window.innerWidth - TIP_WIDTH / 2 - MARGIN),
    );
    setTipStyle({
      position: "fixed",
      left,
      top: b.bottom + 6,
      transform: "translateX(-50%)",
      width: TIP_WIDTH,
      zIndex: 9999,
    });
  }

  function hide() {
    setTipStyle(null);
  }

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: tooltip trigger needs inline span layout, not a button */}
      <span
        ref={ref}
        className={[
          "inline-flex items-center justify-center",
          "cursor-help select-none",
          onDark ? "text-white" : "text-(--fg-muted)",
          "transition-colors duration-150",
        ].join(" ")}
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        role="button"
        aria-label={text}
      >
        <Info size={13} />
      </span>

      {tipStyle &&
        createPortal(
          <div
            className={[
              "px-3 py-2.5",
              "bg-(--bg-white)",
              "border border-(--border-default)",
              "rounded-[3px]",
              "shadow-[0_8px_20px_rgba(10,16,32,0.12),0_2px_6px_rgba(10,16,32,0.06)]",
              "text-[11.5px] leading-[1.5]",
              "text-(--fg-secondary)",
              "pointer-events-none",
            ].join(" ")}
            style={tipStyle}
            role="tooltip"
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
}
